import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';
import { Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomUniformsPage() {
  const { t, lang } = useLanguage();
  const c = t.customUniforms;

  const [formData, setFormData] = useState({
    club_name: '',
    contact_name: '',
    email: '',
    phone: '',
    sport: '',
    estimated_players: '',
    province: '',
    project_description: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/quote`, {
        ...formData,
        estimated_players: parseInt(formData.estimated_players) || 0,
      });
      toast.success(c.form.success);
      setFormData({
        club_name: '',
        contact_name: '',
        email: '',
        phone: '',
        sport: '',
        estimated_players: '',
        province: '',
        project_description: '',
      });
    } catch (error) {
      toast.error(c.form.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="custom-uniforms-page">
      {/* Hero */}
      <section className="relative h-[45vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/custom/hero.webp"
            alt="Custom Uniforms"
            className="w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-[#CE2B37]/20" />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative z-10 text-center px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm text-xs tracking-[6px] text-white/80 uppercase mb-4"
          >
            {c.hero.overline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white tracking-tight"
          >
            {c.hero.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="font-dm text-base text-white/80 mt-4 max-w-2xl mx-auto"
          >
            {c.hero.subtitle}
          </motion.p>
        </div>
      </section>

      <TricolorStripe />

      {/* What is Sublimation */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="sublimation-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight">
                {c.sublimation.title}
              </h2>
              <p className="font-dm text-base text-[#6B6B6B] mt-6 leading-relaxed">
                {c.sublimation.body}
              </p>
              {c.sublimation.body2 && (
                <p className="font-dm text-base text-[#6B6B6B] mt-4 leading-relaxed" data-testid="sublimation-body2">
                  {c.sublimation.body2}
                </p>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-full min-h-[400px]"
            >
              <img
                src="/custom/fabric.webp"
                alt="Custom Mass Sport team kit"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Process Steps */}
      <section className="py-24 md:py-32 bg-[#0D0D0D]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-bebas text-4xl md:text-5xl text-white tracking-tight">
              {c.process.title}
            </h2>
            <p className="font-dm text-base text-white/60 mt-4 max-w-xl mx-auto">
              {c.process.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {c.process.steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="relative"
              >
                <span className="font-bebas text-7xl text-[#CE2B37]/20 absolute -top-4 -left-2">
                  {step.num}
                </span>
                <div className="relative pt-12">
                  <h3 className="font-bebas text-xl text-white tracking-wide mb-4">
                    {step.title}
                  </h3>
                  <p className="font-dm text-sm text-white/60 leading-relaxed">
                    {step.body}
                  </p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px border-t-2 border-dashed border-[#CE2B37]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Benefits Grid */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl text-[#0D0D0D] tracking-tight text-center mb-16"
          >
            {c.benefits.title}
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.benefits.items.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="bg-white border border-[#E8E8E8] p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-[#CE2B37] flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bebas text-lg text-[#0D0D0D] tracking-wide mb-2">
                      {item.title}
                    </h3>
                    <p className="font-dm text-sm text-[#6B6B6B] leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TricolorStripe />

      {/* Quote Form */}
      <section className="py-24 md:py-32 bg-[#CE2B37]" data-testid="quote-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-bebas text-4xl md:text-5xl text-white tracking-tight">
              {c.form.title}
            </h2>
            <p className="font-dm text-base text-white/80 mt-4">
              {c.form.subtitle}
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="quote-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="text"
                name="club_name"
                value={formData.club_name}
                onChange={handleChange}
                placeholder={c.form.fields.clubName}
                required
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200"
                data-testid="quote-club-name"
              />
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder={c.form.fields.contactName}
                required
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200"
                data-testid="quote-contact-name"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={c.form.fields.email}
                required
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200"
                data-testid="quote-email"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={c.form.fields.phone}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200"
                data-testid="quote-phone"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <select
                name="sport"
                value={formData.sport}
                onChange={handleChange}
                required
                className="bg-white/10 border border-white/30 text-white font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200 appearance-none"
                data-testid="quote-sport"
              >
                <option value="" className="text-[#0D0D0D]">{c.form.fields.sport}</option>
                {c.form.sports.map((sport) => (
                  <option key={sport} value={sport} className="text-[#0D0D0D]">{sport}</option>
                ))}
              </select>
              <input
                type="number"
                name="estimated_players"
                value={formData.estimated_players}
                onChange={handleChange}
                placeholder={c.form.fields.players}
                required
                min="1"
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200"
                data-testid="quote-players"
              />
              <select
                name="province"
                value={formData.province}
                onChange={handleChange}
                required
                className="bg-white/10 border border-white/30 text-white font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200 appearance-none"
                data-testid="quote-province"
              >
                <option value="" className="text-[#0D0D0D]">{c.form.fields.province}</option>
                {c.form.provinces.map((prov) => (
                  <option key={prov} value={prov} className="text-[#0D0D0D]">{prov}</option>
                ))}
              </select>
            </div>

            <textarea
              name="project_description"
              value={formData.project_description}
              onChange={handleChange}
              placeholder={c.form.fields.description}
              rows={5}
              className="bg-white/10 border border-white/30 text-white placeholder:text-white/50 font-dm text-sm px-4 py-4 w-full focus:outline-none focus:border-white transition-colors duration-200 resize-none"
              data-testid="quote-description"
            />

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-white text-[#CE2B37] font-bebas text-lg tracking-wider py-4 hover:bg-[#FAFAFA] transition-colors duration-200 disabled:opacity-50"
              data-testid="quote-submit-btn"
            >
              {isLoading ? '...' : c.form.submit}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
